/**
 * Packing slip page detail drawer (Phase 4A).
 *
 * Opens immediately when triggered. Image loads asynchronously with its own
 * spinner so the drawer doesn't feel sluggish. Shows AI extraction + matched
 * PO + outcome. Reprocess action shows live status transition by polling
 * the page detail every 3s while in PENDING/PROCESSING.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlideOutPanel } from '../common';
import {
  dismissPackingSlipPage,
  fetchPackingSlipPage,
  fetchPackingSlipPageImageBlobUrl,
  reprocessPackingSlipPage,
} from '../../api/purchasing';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_STYLES = {
  matched: 'bg-green-100 text-green-800 border-green-200',
  matched_no_parts: 'bg-amber-100 text-amber-800 border-amber-200',
  manual_review: 'bg-amber-100 text-amber-800 border-amber-200',
  unmatched: 'bg-gray-100 text-gray-700 border-gray-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  pending: 'bg-blue-100 text-blue-800 border-blue-200',
  processing: 'bg-blue-100 text-blue-800 border-blue-200',
};

const STATUS_LABELS = {
  matched: 'Matched',
  matched_no_parts: 'Matched / No Parts',
  manual_review: 'Manual Review',
  unmatched: 'Unmatched',
  failed: 'Failed',
  pending: 'Pending',
  processing: 'Processing',
};

const TRANSIENT_STATUSES = new Set(['pending', 'processing']);

// Phase 4B Stage 5: per-line pill + label maps for the Line Items table.
// Keys mirror PackingSlipPageLine.MatchStatus and .MatchStrategy choices.
const LINE_STATUS_STYLES = {
  matched:          'bg-green-100 text-green-800',
  no_po_line:       'bg-red-100 text-red-800',
  already_received: 'bg-yellow-100 text-yellow-800',
  zero_qty:         'bg-gray-100 text-gray-700',
  extracted_only:   'bg-blue-100 text-blue-800',
};

const LINE_STATUS_LABELS = {
  matched:          'Matched',
  no_po_line:       'No PO Line',
  already_received: 'Already Received',
  zero_qty:         'Zero Qty',
  extracted_only:   'Extracted',
};

const LINE_STRATEGY_LABELS = {
  none:                 'None',
  raw_vendor_part:      'PO raw_vendor_part_number',
  vendor_part_fk:       'PO vendor_part FK',
  raw_item_number:      'PO raw_item_number',
  bom_vendor_part:      'BOM vendor_part',
  bom_internal_part:    'BOM internal part #',
  vendor_part_via_part: 'VendorPart → Part → BOM',
};

function StatusBadge({ status, animated = false }) {
  const style = STATUS_STYLES[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  const label = STATUS_LABELS[status] || status;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full border ${style}`}>
      {animated && (
        <span className="w-2 h-2 rounded-full bg-current animate-pulse" />
      )}
      {label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return d;
  }
}

function fmtConfidence(c) {
  if (c === null || c === undefined) return '—';
  const n = parseFloat(c);
  if (Number.isNaN(n)) return '—';
  return `${Math.round(n * 100)}%`;
}

// Phase 4B Stage 5: small pill for per-line match_status. Matches the visual
// rhythm of StatusBadge but flatter (no border, no animated dot) since rows
// are dense.
function LinePill({ status }) {
  const cls = LINE_STATUS_STYLES[status] || 'bg-gray-100 text-gray-700';
  const label = LINE_STATUS_LABELS[status] || status;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

// Phase 4B Stage 5: strip trailing zeros from a decimal qty string, return
// "—" for null/empty/0. "2.0000" -> "2", "2.5000" -> "2.5", "0" -> "—".
// Returning "—" for 0 is intentional per spec: a MATCHED row that received
// nothing in one column (e.g. all-to-stock) should read as "this column
// got nothing", not "this column got literally zero".
function fmtQty(d) {
  if (d === null || d === undefined || d === '') return '—';
  const n = parseFloat(d);
  if (Number.isNaN(n) || n === 0) return '—';
  return n.toString().replace(/\.?0+$/, '');
}

// ---------------------------------------------------------------------------
// Image preview (loads async, own spinner)
// ---------------------------------------------------------------------------

function ImagePreview({ pageId }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const currentBlobRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setBlobUrl(null);

    fetchPackingSlipPageImageBlobUrl(pageId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        // Revoke any previous URL we held for this drawer instance
        if (currentBlobRef.current) {
          URL.revokeObjectURL(currentBlobRef.current);
        }
        currentBlobRef.current = url;
        setBlobUrl(url);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setError('Source PDF could not be retrieved (Gmail attachment may be expired or unavailable).');
        } else {
          setError(`Image render failed (${status || 'network error'}).`);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pageId]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (currentBlobRef.current) {
        URL.revokeObjectURL(currentBlobRef.current);
        currentBlobRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center bg-gray-50 border border-gray-200 rounded-md py-16">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          <span className="text-xs text-gray-500">Loading scan…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-md p-6 text-center">
        <p className="text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
      <img
        src={blobUrl}
        alt="Packing slip scan"
        className="w-full h-auto block"
        loading="lazy"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reprocess history (collapsible)
// ---------------------------------------------------------------------------

function ReprocessHistory({ history }) {
  const [expanded, setExpanded] = useState(false);
  if (!history || history.length === 0) return null;

  return (
    <div className="border border-gray-200 rounded-md">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center justify-between text-sm text-gray-700 hover:bg-gray-50"
      >
        <span>Reprocess history ({history.length})</span>
        <span className="text-xs text-gray-400">{expanded ? '−' : '+'}</span>
      </button>
      {expanded && (
        <div className="border-t border-gray-200 divide-y divide-gray-100">
          {history.map((entry, idx) => (
            <div key={idx} className="px-3 py-2 text-xs text-gray-600">
              <div className="flex justify-between mb-0.5">
                <span className="font-medium">{entry.by || 'unknown'}</span>
                <span>{fmtDate(entry.at)}</span>
              </div>
              <div className="text-gray-500">
                Prior status: <span className="font-mono">{entry.prior_status}</span>
                {entry.prior_error && (
                  <div className="mt-0.5 text-gray-400 truncate" title={entry.prior_error}>
                    {entry.prior_error}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line Items (Phase 4B Stage 5)
// ---------------------------------------------------------------------------

function LineItemsSection({ lines, matchedPoNumber }) {
  const hasRows = Array.isArray(lines) && lines.length > 0;
  return (
    <div className="border border-gray-200 rounded-md">
      <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700">Line items</h3>
      </div>
      {!hasRows ? (
        <div className="px-3 text-sm text-gray-500 italic py-3">
          No line-level data captured for this page.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50/50 text-gray-500">
              <tr>
                <th className="text-left px-2 py-1.5 font-medium">#</th>
                <th className="text-left px-2 py-1.5 font-medium">Part</th>
                <th className="text-right px-2 py-1.5 font-medium">Qty</th>
                <th className="text-left px-2 py-1.5 font-medium">Status</th>
                <th className="text-left px-2 py-1.5 font-medium">PO Line</th>
                <th className="text-left px-2 py-1.5 font-medium">BOM</th>
                <th className="text-right px-2 py-1.5 font-medium">To BOM</th>
                <th className="text-right px-2 py-1.5 font-medium">To Stock</th>
                <th className="text-left px-2 py-1.5 font-medium">Why</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((row) => {
                const partNum = row.extracted_part_number || '—';
                const partTruncated = partNum.length > 24
                  ? partNum.slice(0, 24) + '…'
                  : partNum;
                // Show strategy sub-line for every non-NONE strategy, including
                // matched rows. Operationally useful when investigating an
                // unexpected match or comparing vendor patterns later.
                const strategyLabel = row.match_strategy_used !== 'none'
                  ? LINE_STRATEGY_LABELS[row.match_strategy_used] || row.match_strategy_used
                  : null;
                // PO Line column option A: show the page-level matched PO #
                // when this line matched a PO line, else em-dash. The PO# is
                // the same across all rows but repeating it per-row reinforces
                // which rows reconciled vs which didn't.
                const poLineCell = row.matched_po_line_id
                  ? (matchedPoNumber || '✓')
                  : '—';
                return (
                  <tr key={row.id}>
                    <td className="px-2 py-1 text-gray-400 tabular-nums align-top">
                      {row.extraction_index + 1}
                    </td>
                    <td className="px-2 py-1 font-mono text-gray-900 align-top" title={partNum}>
                      {partTruncated}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums align-top">
                      {fmtQty(row.extracted_qty_shipped)}
                    </td>
                    <td className="px-2 py-1 align-top">
                      <div className="flex flex-col gap-0.5 items-start">
                        <LinePill status={row.match_status} />
                        {strategyLabel && (
                          <span className="text-[10px] text-gray-400 leading-tight">
                            {strategyLabel}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1 font-mono text-gray-700 align-top">
                      {poLineCell}
                    </td>
                    <td
                      className="px-2 py-1 text-gray-700 align-top"
                      title={row.resolved_part_description || ''}
                    >
                      {row.resolved_internal_part_no || '—'}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums align-top">
                      {fmtQty(row.qty_received_to_bom)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums align-top">
                      {fmtQty(row.qty_received_to_stock)}
                    </td>
                    <td className="px-2 py-1 text-gray-600 align-top">
                      {row.failure_reason || ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main drawer
// ---------------------------------------------------------------------------

export default function PackingSlipDetailDrawer({ pageId, open, onClose, onChange }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionInFlight, setActionInFlight] = useState(null); // 'reprocess' | 'dismiss' | null

  const reload = useCallback(async () => {
    if (!pageId) return null;
    try {
      const data = await fetchPackingSlipPage(pageId);
      setPage(data);
      setError('');
      return data;
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load page.';
      setError(msg);
      return null;
    }
  }, [pageId]);

  // Initial load
  useEffect(() => {
    if (!open || !pageId) {
      setPage(null);
      setLoading(true);
      setError('');
      return;
    }
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [open, pageId, reload]);

  // Live polling while page is in a transient state (PENDING/PROCESSING)
  useEffect(() => {
    if (!page || !TRANSIENT_STATUSES.has(page.status)) return undefined;
    const interval = setInterval(() => {
      reload();
    }, 3000);
    return () => clearInterval(interval);
  }, [page, reload]);

  const handleReprocess = async () => {
    if (!page || actionInFlight) return;
    setActionInFlight('reprocess');
    try {
      const updated = await reprocessPackingSlipPage(page.id);
      setPage(updated);
      onChange?.();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Reprocess failed.';
      alert(msg);
    } finally {
      setActionInFlight(null);
    }
  };

  const handleDismiss = async () => {
    if (!page || actionInFlight) return;
    if (!window.confirm('Mark this page as triaged? It will drop from the review queue. Underlying status is preserved.')) return;
    setActionInFlight('dismiss');
    try {
      const updated = await dismissPackingSlipPage(page.id);
      setPage(updated);
      onChange?.();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Dismiss failed.';
      alert(msg);
    } finally {
      setActionInFlight(null);
    }
  };

  const isTransient = page && TRANSIENT_STATUSES.has(page.status);
  const canReprocess = page && !isTransient;
  const canDismiss = page && !page.is_dismissed && !isTransient;

  return (
    <SlideOutPanel
      open={open}
      onClose={onClose}
      title="Packing slip review"
      width="max-w-2xl"
    >
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {!loading && error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && page && (
        <div className="space-y-5">
          {/* Top row: status + meta */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={page.status} animated={isTransient} />
              {page.is_dismissed && (
                <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-full">
                  Dismissed
                </span>
              )}
              <span className="text-xs text-gray-400">
                Page {page.page_number} · {fmtDate(page.created_at)}
              </span>
            </div>
            <div className="text-xs text-gray-400 font-mono truncate">
              {page.id?.slice(0, 8)}
            </div>
          </div>

          {/* Image preview (async) */}
          <ImagePreview pageId={page.id} />

          {/* AI extraction */}
          <div className="border border-gray-200 rounded-md">
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">AI extraction</h3>
            </div>
            <dl className="px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-gray-500">Vendor identified</dt>
              <dd className="text-gray-900">{page.ai_vendor_identified || '—'}</dd>

              <dt className="text-gray-500">Slip PO #</dt>
              <dd className="text-gray-900 font-mono">{page.extracted_po_number || '—'}</dd>

              <dt className="text-gray-500">Slip PO # (normalized)</dt>
              <dd className="text-gray-900 font-mono">{page.normalized_po_number || '—'}</dd>

              <dt className="text-gray-500">AI confidence</dt>
              <dd className="text-gray-900">{fmtConfidence(page.ai_confidence)}</dd>

              <dt className="text-gray-500">AI parts (count)</dt>
              <dd className="text-gray-900">{page.ai_part_count ?? 0}</dd>
            </dl>
            {page.ai_reasoning && (
              <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/50 text-xs text-gray-700">
                <span className="font-medium text-gray-600">Reasoning: </span>
                {page.ai_reasoning}
              </div>
            )}
          </div>

          {/* Matched PO */}
          <div className="border border-gray-200 rounded-md">
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">Matched purchase order</h3>
            </div>
            {page.matched_po_id ? (
              <dl className="px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-gray-500">PO #</dt>
                <dd className="text-gray-900 font-mono">{page.matched_po_number || '—'}</dd>

                <dt className="text-gray-500">Vendor</dt>
                <dd className="text-gray-900">{page.vendor_name || '—'}</dd>

                <dt className="text-gray-500">Job</dt>
                <dd className="text-gray-900">
                  {page.job_id ? (
                    <button
                      onClick={() => navigate(`/jobs/${page.job_id}`)}
                      className="text-blue-600 hover:underline"
                    >
                      Job #{page.job_number}
                    </button>
                  ) : '—'}
                </dd>

                <dt className="text-gray-500">Customer</dt>
                <dd className="text-gray-900">{page.customer_name || '—'}</dd>
              </dl>
            ) : (
              <div className="px-3 py-3 text-sm text-gray-500 italic">
                No PO matched.
              </div>
            )}
          </div>

          {/* Receive outcome */}
          <div className="border border-gray-200 rounded-md">
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">Receive outcome</h3>
            </div>
            <dl className="px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-gray-500">Parts received</dt>
              <dd className="text-gray-900 font-mono">{page.parts_received_count ?? 0}</dd>

              <dt className="text-gray-500">Pick ticket generated</dt>
              <dd className="text-gray-900">{page.pick_ticket_generated ? 'Yes' : 'No'}</dd>
            </dl>
          </div>

          {/* Line items (Phase 4B Stage 5) */}
          <LineItemsSection
            lines={page.lines}
            matchedPoNumber={page.matched_po_number}
          />

          {/* Error message */}
          {page.error_message && (
            <div className="border border-red-200 rounded-md bg-red-50">
              <div className="px-3 py-2 border-b border-red-100">
                <h3 className="text-sm font-semibold text-red-700">Error / explanation</h3>
              </div>
              <pre className="px-3 py-2 text-xs text-red-800 whitespace-pre-wrap font-sans">
                {page.error_message}
              </pre>
            </div>
          )}

          {/* Dismiss attribution */}
          {page.is_dismissed && (
            <div className="border border-gray-200 rounded-md bg-gray-50/50">
              <div className="px-3 py-2 text-xs text-gray-600">
                Dismissed by <span className="font-medium">{page.dismissed_by_email || '—'}</span> on {fmtDate(page.dismissed_at)}
              </div>
            </div>
          )}

          {/* Reprocess history */}
          <ReprocessHistory history={page.reprocess_history} />

          {/* Action buttons (sticky to bottom) */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
            <button
              onClick={handleDismiss}
              disabled={!canDismiss || actionInFlight !== null}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionInFlight === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
            </button>
            <button
              onClick={handleReprocess}
              disabled={!canReprocess || actionInFlight !== null}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionInFlight === 'reprocess' ? 'Queueing…' : isTransient ? 'Processing…' : 'Reprocess'}
            </button>
          </div>
        </div>
      )}
    </SlideOutPanel>
  );
}
